<template>
  <lila-default-template>
    <template #mainContent>
      <div :class="loadingContent ? 'loading large' : ''">
        <form
          v-show="!loadingContent"
          class="container add-unit-page"
          @submit.prevent="submitForm()"
        >
          <h1 class="title">
            {{ isEdit ? "Editar Unidade / Equipe" : "Adicionar Unidade / Equipe" }}
          </h1>

          <lila-message :alert-data="alert" />

          <div class="add-unit-form">
            <div class="add-unit-field">
              <label class="add-unit-label">Nome da Unidade / Equipe</label>
              <lila-input
                v-model="v.data.name.$model"
                placeholder="Digite aqui"
                :validation="v.data.name"
              />
            </div>

            <div
              v-if="!isEdit"
              class="add-unit-field add-unit-parent-field"
            >
              <label class="add-unit-label">
                Unidade superior<span class="add-unit-optional"> (opcional)</span>
              </label>

              <div class="add-unit-parent">
                <button
                  type="button"
                  class="add-unit-parent-btn"
                  @click.stop="toggleParentPicker"
                >
                  <span class="add-unit-parent-value">{{ parentLabel }}</span>
                </button>

                <div
                  v-if="parentPickerOpen"
                  class="add-unit-parent-popover"
                >
                  <unit-parent-picker
                    :tree="parentTree"
                    :root-allowed="true"
                    :current-id="selectedParent ? selectedParent.id : null"
                    title="Onde criar a nova unidade/equipe"
                    @select="onParentSelected"
                  />
                </div>
              </div>

              <p class="add-unit-parent-hint">
                Deixe em “Instituição” para criar uma unidade nova no topo (sem ficar abaixo de outra), ou escolha uma unidade/equipe como superior.
              </p>
            </div>

            <div
              v-if="isEdit"
              class="add-unit-field add-unit-members-field"
            >
              <div class="add-unit-search">
                <label class="add-unit-label">
                  Pesquisar em Profissionais e Disponíveis
                </label>
                <lila-input
                  v-model="search"
                  placeholder="Pesquisar"
                />
              </div>

              <div
                :class="loadingMembers ? 'loading' : ''"
                class="add-unit-columns"
              >
                <section
                  v-if="teamProfessionals.length"
                  class="add-unit-column"
                >
                  <header class="add-unit-column-head">
                    <h2 class="add-unit-column-title">
                      Profissionais
                    </h2>
                    <span class="add-unit-badge">
                      {{ teamProfessionals.length }}
                    </span>
                  </header>

                  <ul class="add-unit-list custom-scroll">
                    <li
                      v-for="professional in filteredTeamProfessionals"
                      :key="professional.id"
                      class="add-unit-member-card"
                    >
                      <p class="add-unit-member-name">
                        {{ professional.name }}
                      </p>
                      <div class="add-unit-member-toggle">
                        <span class="add-unit-member-toggle-label">
                          Membro da equipe
                        </span>
                        <label class="au-switch">
                          <input
                            type="checkbox"
                            :checked="professional.isMember"
                            :disabled="isMembershipSaving(professional)"
                            @change="toggleMember(professional, $event.target.checked)"
                          >
                          <span class="au-slider" />
                        </label>
                      </div>

                      <div class="add-unit-member-toggle">
                        <span class="add-unit-member-toggle-label">
                          Navegador
                        </span>
                        <label class="au-switch">
                          <input
                            type="checkbox"
                            :checked="professional.isNavigator"
                            :disabled="isMembershipSaving(professional)"
                            @change="toggleNavigator(professional, $event.target.checked)"
                          >
                          <span class="au-slider" />
                        </label>
                      </div>
                    </li>
                  </ul>
                </section>

                <section
                  v-if="externalProfessionals.length"
                  class="add-unit-column"
                >
                  <header class="add-unit-column-head">
                    <h2 class="add-unit-column-title">
                      Profissionais Disponíveis
                    </h2>
                    <span class="add-unit-badge">
                      {{ externalProfessionals.length }}
                    </span>
                  </header>

                  <ul class="add-unit-list custom-scroll">
                    <li
                      v-for="professional in filteredExternalProfessionals"
                      :key="professional.id"
                      class="add-unit-member-card"
                    >
                      <p class="add-unit-member-name">
                        {{ professional.name }}
                      </p>
                      <div class="add-unit-member-toggle">
                        <span class="add-unit-member-toggle-label">
                          Membro da equipe
                        </span>
                        <label class="au-switch">
                          <input
                            type="checkbox"
                            :checked="professional.isMember"
                            :disabled="isMembershipSaving(professional)"
                            @change="toggleMember(professional, $event.target.checked)"
                          >
                          <span class="au-slider" />
                        </label>
                      </div>
                    </li>
                  </ul>
                </section>
              </div>

              <p
                v-if="!loadingMembers && professionals.length === 0"
                class="add-unit-members-empty"
              >
                Nenhum profissional encontrado.
              </p>
            </div>

            <div class="add-unit-actions">
              <lila-button
                id="save-unit"
                type="submit"
                title="Salvar"
                :loading="loading || checkingPatients"
                :second-button="{
                  type: 'route',
                  route: 'units',
                  title: 'Cancelar',
                  class: 'cancel'
                }"
              />
            </div>
          </div>
        </form>

        <div
          v-if="confirmModal.show"
          class="au-modal-overlay"
          @click="cancelConfirm"
        >
          <div
            class="au-modal"
            @click.stop
          >
            <h2 class="au-modal-title">
              Transformar equipe em unidade?
            </h2>
            <p class="au-modal-text">
              A equipe <strong>{{ selectedParent ? selectedParent.name : "" }}</strong>
              será transformada em uma <strong>unidade</strong>.
              {{ confirmModal.patientCount }}
              {{ confirmModal.patientCount === 1 ? "paciente atualmente vinculado" : "pacientes atualmente vinculados" }}
              a essa equipe
              {{ confirmModal.patientCount === 1 ? "será transferido" : "serão transferidos" }}
              para a nova equipe que está sendo criada.
            </p>
            <div class="au-modal-actions">
              <button
                type="button"
                class="au-modal-btn au-modal-ghost"
                @click="cancelConfirm"
              >
                Cancelar
              </button>
              <button
                type="button"
                class="au-modal-btn au-modal-primary"
                :disabled="loading"
                @click="confirmCreate"
              >
                {{ loading ? "Criando…" : "Confirmar e criar" }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </template>
  </lila-default-template>
</template>

<script>
import DefaultTemplate from "@components/template/DefaultTemplate.vue";
import Input from "@components/shared/fields/Input.vue";
import Button from "@components/shared/button/Button.vue";
import Message from "@components/shared/message/Message.vue";
import UnitParentPicker from "@components/shared/lila/UnitParentPicker.vue";

import { required, maxLength } from "@vuelidate/validators";
import decodeToken from "@/js/decodeToken";
import useTeamService from "@/composables/useTeamService";
import useAuth from "@/composables/useAuth";
import { ProfessionalRole } from "@js/professionalProfile";

export default {
  components: {
    "lila-default-template": DefaultTemplate,
    "lila-input": Input,
    "lila-button": Button,
    "lila-message": Message,
    "unit-parent-picker": UnitParentPicker
  },
  setup() {
    return { team: useTeamService() };
  },
  data() {
    return {
      data: {
        name: ""
      },
      search: "",
      professionals: [],
      savingMembershipIds: [],
      membershipSavePromises: {},
      loadingMembers: false,
      loading: false,
      loadingContent: false,
      transferTargets: [],
      selectedParent: null,
      parentPickerOpen: false,
      checkingPatients: false,
      confirmModal: {
        show: false,
        patientCount: 0
      },
      alert: {
        message: "",
        type: "",
        show: false
      }
    };
  },
  validations() {
    return {
      data: {
        name: { required, maxLength: maxLength(255) }
      }
    };
  },
  computed: {
    unitId() {
      return this.$route.params.id;
    },
    isEdit() {
      return !!this.unitId;
    },
    teamProfessionals() {
      return this.professionals.filter((professional) => professional.isMember);
    },
    externalProfessionals() {
      return this.professionals.filter((professional) => !professional.isMember);
    },
    filteredTeamProfessionals() {
      return this.applySearch(this.teamProfessionals);
    },
    filteredExternalProfessionals() {
      return this.applySearch(this.externalProfessionals);
    },
    parentLabel() {
      return this.selectedParent ? this.selectedParent.name : "Instituição";
    },
    parentTree() {
      const roots = [];
      const findOrAdd = (children, node, isTeam) => {
        let existing = children.find(
          (child) => String(child.id) === String(node.id)
        );
        if (!existing) {
          existing = { id: node.id, name: node.name, isTeam, children: [] };
          children.push(existing);
        }
        return existing;
      };
      this.transferTargets.forEach((target) => {
        let children = roots;
        (target.path || []).forEach((unit) => {
          children = findOrAdd(children, unit, false).children;
        });
        findOrAdd(children, target, target.isTeam);
      });
      return roots;
    }
  },
  created() {
    if (this.professionalRole() < 1) {
      this.$router.replace({ name: "units" });
      return;
    }

    if (this.isEdit) {
      this.loadUnit();
      this.loadProfessionals();
    } else {
      this.getTransferTargets();
    }
  },
  mounted() {
    document.addEventListener("click", this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleClickOutside);
  },
  methods: {
    professionalRole() {
      const session = this.$Session.get(this.$userSessionName);
      const decoded = session ? decodeToken(session.token.Value) : null;
      return decoded ? Number(decoded.ProfessionalRole ?? 0) : 0;
    },

    async loadUnit() {
      this.loadingContent = true;
      try {
        const res = await this.team.getById(this.unitId);
        const item = res.data || {};
        this.data.name = this.$Utilities.getValueIgnoreCase(item, "Name") || "";
      } catch (error) {
        if (error.response) this.$Utilities.verifyToken(error.response.status);
        this.$Utilities.controlAlert(
          error.response ? error.response.data : null,
          this.alert,
          "danger",
          "Não foi possível carregar a unidade."
        );
      } finally {
        this.loadingContent = false;
      }
    },

    async loadProfessionals() {
      this.loadingMembers = true;
      try {
        const res = await this.team.getProfessionals(this.unitId);
        const data = res.data || {};
        const members =
          this.$Utilities.getValueIgnoreCase(data, "Professionals") || [];
        const available =
          this.$Utilities.getValueIgnoreCase(data, "AvailableProfessionals") ||
          [];

        this.professionals = [...members, ...available]
          .filter((item) => item != null)
          .map((item) => this.mapMember(item));
      } catch (error) {
        if (error.response) this.$Utilities.verifyToken(error.response.status);
        this.$Utilities.controlAlert(
          error.response ? error.response.data : null,
          this.alert,
          "danger",
          "Não foi possível carregar os profissionais."
        );
      } finally {
        this.loadingMembers = false;
      }
    },

    mapMember(item) {
      const isMember = Boolean(
        this.$Utilities.getValueIgnoreCase(item, "IsInTeam")
      );
      const isNavigator = Boolean(
        this.$Utilities.getValueIgnoreCase(item, "IsNavigator")
      );
      return {
        id: this.$Utilities.getValueIgnoreCase(item, "Id"),
        name: this.$Utilities.getValueIgnoreCase(item, "Name") || "",
        isMember,
        originalMember: isMember,
        isNavigator,
        originalNavigator: isNavigator
      };
    },

    applySearch(list) {
      const query = this.search.trim().toLowerCase();
      if (!query) return list;
      return list.filter((professional) =>
        professional.name.toLowerCase().includes(query)
      );
    },

    isMembershipSaving(professional) {
      return this.savingMembershipIds.includes(professional.id);
    },

    toggleMember(professional, checked) {
      const previous = {
        isMember: professional.isMember,
        isNavigator: professional.isNavigator
      };
      professional.isMember = checked;
      if (!checked) professional.isNavigator = false;
      this.saveProfessionalMembership(professional, previous);
    },

    toggleNavigator(professional, checked) {
      const previous = {
        isMember: professional.isMember,
        isNavigator: professional.isNavigator
      };
      professional.isNavigator = checked;
      this.saveProfessionalMembership(professional, previous);
    },

    saveProfessionalMembership(professional, previous) {
      const payload = {
        Professionals: [
          {
            Id: professional.id,
            IsInTeam: professional.isMember,
            IsNavigator: professional.isMember && professional.isNavigator
          }
        ]
      };

      if (!this.savingMembershipIds.includes(professional.id)) {
        this.savingMembershipIds.push(professional.id);
      }

      const savePromise = this.team
        .updateProfessionals(this.unitId, payload)
        .then(() => {
          professional.originalMember = professional.isMember;
          professional.originalNavigator = professional.isNavigator;
        })
        .catch((error) => {
          professional.isMember = previous.isMember;
          professional.isNavigator = previous.isNavigator;
          this.handleSaveError(error, "Erro ao salvar profissional na equipe.");
        })
        .finally(() => {
          this.savingMembershipIds = this.savingMembershipIds.filter(
            (id) => id !== professional.id
          );
          delete this.membershipSavePromises[professional.id];
        });

      this.membershipSavePromises[professional.id] = savePromise;
      return savePromise;
    },

    saveMembership() {
      const changed = this.professionals.filter(
        (professional) =>
          professional.isMember !== professional.originalMember ||
          professional.isNavigator !== professional.originalNavigator
      );
      if (changed.length === 0) return Promise.resolve();

      const payload = {
        Professionals: changed.map((professional) => ({
          Id: professional.id,
          IsInTeam: professional.isMember,
          IsNavigator: professional.isMember && professional.isNavigator
        }))
      };

      return this.team.updateProfessionals(this.unitId, payload);
    },

    async getTransferTargets() {
      try {
        const res = await this.team.list();
        const list = Array.isArray(res.data) ? res.data : res.data.Data || [];
        this.transferTargets = this.$Utilities.teamsWithPath(list, { includeUnits: true });
      } catch (error) {
        console.error("Erro ao carregar a árvore de unidades:", error);
      }
    },

    toggleParentPicker() {
      this.parentPickerOpen = !this.parentPickerOpen;
    },

    handleClickOutside(event) {
      if (!this.parentPickerOpen) return;
      const popover = document.querySelector(".add-unit-parent-popover");
      const trigger = event.target.closest
        ? event.target.closest(".add-unit-parent-btn")
        : null;
      if ((!popover || !popover.contains(event.target)) && !trigger) {
        this.parentPickerOpen = false;
      }
    },

    onParentSelected(node) {
      this.selectedParent = node;
      this.parentPickerOpen = false;
    },

    submitForm() {
      this.alert.show = false;

      this.v.$touch();
      if (this.v.$invalid) return;

      if (this.isEdit) {
        this.saveEdit();
        return;
      }

      if (this.selectedParent && this.selectedParent.isTeam) {
        this.checkPatientsThenCreate();
        return;
      }

      this.createUnit();
    },

    async checkPatientsThenCreate() {
      this.checkingPatients = true;
      try {
        const res = await this.team.hasPatients(this.selectedParent.id);
        const hasPatients = Boolean(
          this.$Utilities.getValueIgnoreCase(res.data, "HasPatients")
        );
        const count = Number(
          this.$Utilities.getValueIgnoreCase(res.data, "PatientCount") ?? 0
        );

        if (hasPatients) {
          this.confirmModal = { show: true, patientCount: count };
        } else {
          this.createUnit();
        }
      } catch (error) {
        this.handleSaveError(error, "Não foi possível verificar os pacientes da equipe.");
      } finally {
        this.checkingPatients = false;
      }
    },

    confirmCreate() {
      this.createUnit();
    },

    cancelConfirm() {
      this.confirmModal.show = false;
    },

    createUnit() {
      this.loading = true;
      const payload = {
        name: this.data.name,
        parent: this.selectedParent ? this.selectedParent.id : null
      };

      this.team
        .create(payload)
        .then(async (res) => {
          if (!payload.parent && this.professionalRole() === ProfessionalRole.UnitAdmin) {
            await this.refreshSessionScope();
          }

          const created = res && res.data;
          if (created) {
            try {
              sessionStorage.setItem(
                "lila.createdTeam",
                JSON.stringify(created)
              );
            } catch (e) {
              console.warn("Não foi possível guardar a unidade criada no sessionStorage:", e);
            }
          }

          this.$router.push({
            name: "units",
            query: {
              customMessageType: "success",
              customMessage: "Unidade cadastrada com sucesso."
            }
          });
        })
        .catch((error) =>
          this.handleSaveError(error, "Erro ao salvar unidade.")
        )
        .finally(() => {
          this.loading = false;
          this.confirmModal.show = false;
        });
    },

    async refreshSessionScope() {
      try {
        await useAuth().refreshLogin();
      } catch (error) {
        console.error("Erro ao renovar a sessão após criar a unidade:", error);
      }
    },

    saveEdit() {
      this.loading = true;
      this.team
        .update(this.unitId, { name: this.data.name })
        .then(() =>
          Promise.allSettled(Object.values(this.membershipSavePromises))
        )
        .then(() => {
          this.$router.push({
            name: "units",
            query: {
              customMessageType: "success",
              customMessage: "Unidade atualizada com sucesso."
            }
          });
        })
        .catch((error) =>
          this.handleSaveError(error, "Erro ao salvar unidade.")
        )
        .finally(() => (this.loading = false));
    },

    handleSaveError(error, fallback) {
      this.alert.type = "warning";

      if (error.response && error.response.data) {
        const apiMessage = error.response.data.Message || fallback;
        if (
          error.response.data.Errors &&
          Array.isArray(error.response.data.Errors)
        ) {
          this.alert.message = `${apiMessage} Detalhes: ${error.response.data.Errors.join(
            " "
          )}`;
        } else {
          this.alert.message = apiMessage;
        }
      } else {
        this.alert.message = error.message || "Ocorreu um erro desconhecido.";
      }

      this.$Utilities.controlAlert(this.alert.message, this.alert);
      this.$Utilities.verifyToken(error.response ? error.response.status : null);
    }
  }
};
</script>

<style lang="scss">
$lila-primary: #4f3f82;

.add-unit-page {
  padding-top: 10px;
}

.add-unit-page .title {
  color: $lila-primary;
  font-weight: 700;
  margin-bottom: 24px;
}

.add-unit-page .add-unit-field {
  margin-bottom: 18px;
  max-width: 480px;
}

.add-unit-page .add-unit-field.add-unit-members-field {
  max-width: none;
}

.add-unit-page .add-unit-label {
  display: block;
  font-size: 13px;
  color: #6b6b6b;
  margin-bottom: 8px;
}

.add-unit-page .add-unit-actions {
  margin-top: 26px;
  margin-bottom: 40px;
}

.add-unit-page .add-unit-optional {
  color: #9a9a9a;
  font-weight: 400;
}

.add-unit-page .add-unit-parent {
  position: relative;
  max-width: 480px;
}

.add-unit-page .add-unit-parent-btn {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  height: 45px;
  padding: 0 40px 0 20px;
  border-radius: 45px;
  border: 1px solid #ffffff;
  background: #ffffff;
  font-size: 16px;
  color: #131313;
  cursor: pointer;
  text-align: left;
}

.add-unit-page .add-unit-parent-btn::after {
  content: "";
  position: absolute;
  right: 20px;
  top: 18px;
  width: 7px;
  height: 7px;
  border: 2px solid #839097;
  border-top: 0;
  border-left: 0;
  transform: rotate(45deg);
  pointer-events: none;
}

.add-unit-page .add-unit-parent-value {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.add-unit-page .add-unit-parent-popover {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 600;
}

.add-unit-page .add-unit-parent-hint {
  margin-top: 8px;
  font-size: 12px;
  color: #9a9a9a;
  max-width: 480px;
}

.add-unit-page .au-modal-overlay,
.au-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(36, 31, 51, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.au-modal {
  width: 460px;
  max-width: 100%;
  background: #ffffff;
  border-radius: 16px;
  padding: 28px;
  box-shadow: 0 20px 50px rgba(36, 31, 51, 0.3);
}

.au-modal-title {
  margin: 0 0 14px;
  font-size: 20px;
  font-weight: 700;
  color: #4f3f82;
}

.au-modal-text {
  margin: 0 0 24px;
  font-size: 14px;
  line-height: 1.6;
  color: #3a3a3a;
}

.au-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.au-modal-btn {
  border: none;
  border-radius: 9px;
  padding: 10px 18px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.au-modal-ghost {
  background: transparent;
  color: #6b6680;
}

.au-modal-primary {
  background: #9a397d;
  color: #ffffff;
}

.au-modal-primary:disabled {
  opacity: 0.6;
  cursor: default;
}

.add-unit-page .add-unit-search {
  max-width: 480px;
  margin-bottom: 22px;
}

.add-unit-page .add-unit-columns {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 32px;
  align-items: start;
  max-width: 1400px;
  margin-bottom: 8px;
}

.add-unit-page .add-unit-column {
  display: flex;
  flex-direction: column;
  min-height: 320px;
}

.add-unit-page .add-unit-column-head {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 2px solid #e0e0e0;
}

.add-unit-page .add-unit-column-title {
  margin: 0;
  color: #2c2c2c;
  font-size: 18px;
  font-weight: 600;
}

.add-unit-page .add-unit-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 28px;
  padding: 0 10px;
  border-radius: 14px;
  background: #9a397d;
  color: #ffffff;
  font-size: 13px;
  font-weight: 600;
  line-height: 1;
  box-shadow: 0 2px 4px rgba(154, 57, 125, 0.2);
}

.add-unit-page .add-unit-list {
  list-style: none;
  margin: 0;
  padding: 0 8px 0 0;
  max-height: 600px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.add-unit-page .add-unit-member-card {
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(79, 63, 130, 0.08);
  padding: 16px 20px;
}

.add-unit-page .add-unit-member-name {
  font-size: 16px;
  font-weight: 600;
  color: #2c2c2c;
  margin-bottom: 14px;
}

.add-unit-page .add-unit-member-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 0;
}

.add-unit-page .add-unit-member-toggle + .add-unit-member-toggle {
  margin-top: 10px;
}

.add-unit-page .add-unit-member-toggle-label {
  font-size: 15px;
  font-weight: 300;
  color: #6b6b6b;
}

.add-unit-page .add-unit-members-empty {
  padding: 18px;
  text-align: center;
  color: #9a9a9a;
  font-size: 14px;
}

.add-unit-page .au-switch {
  display: inline-block;
  position: relative;
  width: 45px;
  height: 22px;
  flex: none;
}

.add-unit-page .au-switch input {
  display: none;
}

.add-unit-page .au-slider {
  position: absolute;
  inset: 0;
  background: #e6e4ee;
  border-radius: 34px;
  cursor: pointer;
  transition: 0.4s;
}

.add-unit-page .au-slider::before {
  content: "";
  position: absolute;
  left: 4px;
  bottom: 3px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #ffffff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  transition: 0.4s;
}

.add-unit-page .au-switch input:checked + .au-slider {
  background: #9a397d;
}

.add-unit-page .au-switch input:checked + .au-slider::before {
  transform: translateX(21px);
}

@media (max-width: 1024px) {
  .add-unit-page .add-unit-columns {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .add-unit-page .add-unit-list {
    max-height: 400px;
  }
}
</style>
